import { useMemo, useState } from 'react';
import { List, ListItem, ListItemButton, ListItemText, ListSubheader, Box, TextField, CircularProgress, InputAdornment, Card } from '@mui/material';
import { ReduxState, useListContext } from 'ra-core';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { genUrlWithHashid } from '@/utils/hashid';
import SearchIcon from '@mui/icons-material/Search';
import { RecPatch } from '@/dataProvider/tools';
import { EnumOptionResponseDto } from '@/gen/api/governance';


const groupByFirstLetter = (databases: EnumOptionResponseDto[]) => {
  const groups: Record<string, EnumOptionResponseDto[]> = {};

  databases.forEach(db => {
    const firstChar = (db.text ?? '').charAt(0).toUpperCase();
    const letter = /[A-Z]/.test(firstChar) ? firstChar : '#';

    if (!groups[letter]) {
      groups[letter] = [];
    }
    groups[letter].push(db);
  });

  // Sort within groups
  Object.keys(groups).forEach(letter => {
    groups[letter].sort((a, b) => (a.text ?? '').localeCompare((b.text ?? '')));
  });

  return groups;
};

const AlphabeticalDatabaseList = (): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();

  const listCtx = useListContext<EnumOptionResponseDto&RecPatch>()
  const { data, loaded, error } = listCtx;
  const loading = useSelector<ReduxState, boolean>(store => !!store.admin.loading)
  const databases = Object.values(data || {});

  const filteredDatabases = useMemo(() => {
    if (!searchQuery) return databases;

    return databases.filter(db =>
      (db.text ?? '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [databases, searchQuery]);

  const grouped = useMemo(() => {
    return groupByFirstLetter(filteredDatabases);
  }, [filteredDatabases]);

  const sortedLetters = useMemo(() => {
    return Object.keys(grouped).sort((a, b) => {
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b);
    });
  }, [grouped]);

  const handleNavigate = (databaseId: number, databaseName: string) => {
    const hashId = genUrlWithHashid("$0", databaseId, databaseName);
    dispatch(push(`/governance/database-semantic/${hashId}/edit`));
  };

  if (!loaded) {
    return <div>Loading databases...</div>;
  }

  if (error) {
    return (
      <Box sx={{ p: 4, color: 'error.main' }}>
        Error loading databases
      </Box>
    );
  }

  return (
    <Box sx={{
      pt: 2, pb: 4, px: 2,
      borderRadius: 1,
      bgcolor: 'background.paper',
      '& div.search-box': {
        pb: 2,
        bgcolor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 2,
      },

      '& ul.MuiList-root': {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        width: '100%',

        '& > li.MuiListSubheader-root': { 
          display: 'block',
          width: '100%', 
          fontWeight: 'bold',
          top: 56,
        },
        '& > li.MuiListItem-root': {
          minWidth: '32%',
          width: 'unset',
        },
      }
    }} >
      {/* Search Field */}
      <Box width="100%" className='search-box' >
        <Box sx={{ width: 280 }} >
          <TextField placeholder="Search databases" fullWidth size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {(!loaded || loading) ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
                </InputAdornment>),
            }}
          />
        </Box>
      </Box>

      {/* Alphabetical List */}
      {sortedLetters.map(letter => (
        <List
          key={letter}
          subheader={
            <ListSubheader component="li" id={`letter-${letter}-header`}>
              {letter}
            </ListSubheader>
          }
        >
          {grouped[letter].map(database => (
            <ListItem key={database.key} disablePadding>
              <ListItemButton
                onClick={() => handleNavigate(database.key ?? 0, database.text ?? '')}
                data-testid={`database-${database.key}`}
              >
                <ListItemText primary={database.text} primaryTypographyProps={{ color: 'textPrimary' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ))}

      {sortedLetters.length === 0 && (
        (!loaded || loading) ? (
          <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            No databases found
          </Box>
        ))}
    </Box>
  );
};

export default AlphabeticalDatabaseList;
